/**
 * CollapsibleFilterGroup - Filter group with collapse/expand toggle
 *
 * Shows collapsed state with active count badge, expands on click.
 * Used in filter panels to organize filter chips into collapsible sections.
 */
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

export interface CollapsibleFilterGroupProps {
  /** Group label text */
  label: string;
  /** Whether the group is collapsed */
  isCollapsed: boolean;
  /** Number of active filters in this group (shown as badge when collapsed) */
  activeCount: number;
  /** Toggle collapse state */
  onToggle: () => void;
  /** Filter chips to display when expanded */
  children: ReactNode;
  /** When locked, header is non-interactive and children always render (used when filters are active) */
  isLocked?: boolean;
}

export default function CollapsibleFilterGroup({
  label,
  isCollapsed,
  activeCount,
  onToggle,
  children,
  isLocked = false,
}: CollapsibleFilterGroupProps): ReactElement {
  // When locked, always show children regardless of collapsed state
  const showChildren = isLocked || !isCollapsed;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={isLocked ? undefined : onToggle}
        disabled={isLocked}
        className={clsx(
          'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs tracking-wider uppercase transition-colors',
          isLocked
            ? 'cursor-default text-gray-500'
            : 'hover:bg-slate/50 text-ash hover:text-silver focus-visible:ring-aether focus-visible:ring-offset-void focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
        )}
        aria-expanded={showChildren}
        aria-disabled={isLocked}
        aria-label={`${label} filters${activeCount > 0 ? ` (${activeCount} active)` : ''}${isLocked ? ' (locked)' : ''}`}
      >
        {!isLocked && (isCollapsed ? <IoChevronForward className="h-3 w-3" /> : <IoChevronDown className="h-3 w-3" />)}
        {isLocked && <IoChevronDown className="h-3 w-3 opacity-40" />}
        <span>{label}</span>
        {!showChildren && activeCount > 0 && (
          <span className="bg-aether ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white">{activeCount}</span>
        )}
      </button>
      {showChildren && <div className="flex items-center gap-1">{children}</div>}
    </div>
  );
}
